/**
 * Karty / Seznam segmented density toggle (lg+ only).
 * Composition mirrors admin EntityListFindToolbar view toggle — customer tokens only.
 */

import { useTranslation } from 'react-i18next';
import type { DirectoryDensityMode } from './directoryDensity.js';

export interface DirectoryDensityToggleProps {
  readonly density: DirectoryDensityMode;
  readonly onDensityChange: (mode: DirectoryDensityMode) => void;
  /**
   * When false, renders nothing (compact &lt;lg). Parent should pass
   * `showToggle` from `useDirectoryDensity`.
   */
  readonly visible?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

const SEGMENT_BASE = [
  'inline-flex min-h-11 min-w-11 flex-1 items-center justify-center rounded-md px-3 text-sm font-medium',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-[var(--color-focus-ring)]',
  'transition-colors duration-150 motion-reduce:transition-none',
].join(' ');

function segmentClass(active: boolean): string {
  return [
    SEGMENT_BASE,
    active
      ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]'
      : 'bg-transparent text-[var(--color-on-surface-muted)] hover:bg-[var(--color-surface-hover)]',
  ].join(' ');
}

export function DirectoryDensityToggle(
  props: DirectoryDensityToggleProps,
): JSX.Element | null {
  const {
    density,
    onDensityChange,
    visible = true,
    className,
    testId = 'directory-density-toggle',
  } = props;
  const { t } = useTranslation('customer');

  if (!visible) {
    return null;
  }

  return (
    <div
      className={[
        'flex w-full shrink-0 gap-1 rounded-lg border border-[var(--color-border)]',
        'bg-[var(--color-surface)] p-1 sm:w-auto sm:min-w-[12rem]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="group"
      aria-label={t('directory.density.toggleAria')}
      data-testid={testId}
    >
      <button
        type="button"
        className={segmentClass(density === 'list')}
        aria-pressed={density === 'list'}
        aria-label={t('directory.density.listAria')}
        title={t('directory.density.list')}
        data-testid={`${testId}-list`}
        onClick={() => {
          onDensityChange('list');
        }}
      >
        {t('directory.density.list')}
      </button>
      <button
        type="button"
        className={segmentClass(density === 'cards')}
        aria-pressed={density === 'cards'}
        aria-label={t('directory.density.cardsAria')}
        title={t('directory.density.cards')}
        data-testid={`${testId}-cards`}
        onClick={() => {
          onDensityChange('cards');
        }}
      >
        {t('directory.density.cards')}
      </button>
    </div>
  );
}
